import { useState } from 'react';
import {
  saveBusinessSecrets,
  saveCRMSecrets,
  saveOnboardingSecrets,
  OnboardingSecretsPayload,
} from '../../../../api/organizationSecrets';

/**
 * Hook for saving onboarding secrets to Google Secret Manager
 * instead of saving them directly to Firestore.
 *
 * Usage in onboarding stages:
 * - Stage 4: saveBusinessTaxSecrets() for Twilio EIN/CBN
 * - Stage 5: saveCRMCredentials() for CRM integration secrets
 *
 * Note: Errors are silently logged and do not block the onboarding flow.
 */
export const useOnboardingSecrets = () => {
  const [isPending, setIsPending] = useState(false);

  /**
   * Save Stage 4 business verification secrets (EIN/CBN)
   */
  const saveBusinessTaxSecrets = async (
    orgId: string,
    data: { ein_cbn?: string }
  ): Promise<boolean> => {
    // Only save if there's an EIN/CBN to save
    if (!data.ein_cbn) {
      return true; // Nothing to save, that's okay
    }

    setIsPending(true);
    try {
      await saveBusinessSecrets(orgId, data);
      return true;
    } catch (error) {
      // Silently log error - don't block onboarding flow
      console.error('Error saving business tax secrets:', error);
      return true; // Continue despite error
    } finally {
      setIsPending(false);
    }
  };

  /**
   * Save Stage 5 CRM credentials
   */
  const saveCRMCredentials = async (
    orgId: string,
    crmEntries: Array<{
      crm_type: string;
      crm_other_name?: string;
      super_admin_api_key?: string;
      login_url?: string;
      super_admin_username?: string;
      super_admin_password?: string;
      two_fa_contact_type?: 'me' | 'other';
      two_fa_contact_name?: string;
      two_fa_contact_email?: string;
      two_fa_contact_phone?: string;
      brivity_website_login_url?: string;
      brivity_username?: string;
      brivity_password?: string;
    }>
  ): Promise<boolean> => {
    // Filter to only entries that have sensitive data to save
    // FUB entries don't have credentials here (they're handled separately)
    const entriesWithSecrets = crmEntries.filter(
      (entry) =>
        entry.crm_type !== 'fub' &&
        (entry.super_admin_api_key ||
          entry.super_admin_username ||
          entry.super_admin_password ||
          entry.brivity_username ||
          entry.brivity_password)
    );

    if (entriesWithSecrets.length === 0) {
      return true; // Nothing to save, that's okay
    }

    setIsPending(true);
    try {
      await saveCRMSecrets(orgId, entriesWithSecrets);
      return true;
    } catch (error) {
      // Silently log error - don't block onboarding flow
      console.error('Error saving CRM credentials:', error);
      return true; // Continue despite error
    } finally {
      setIsPending(false);
    }
  };

  /**
   * Save all onboarding secrets at once (for bulk save scenarios)
   */
  const saveAllSecrets = async (
    orgId: string,
    secrets: OnboardingSecretsPayload
  ): Promise<boolean> => {
    setIsPending(true);
    try {
      await saveOnboardingSecrets(orgId, secrets);
      return true;
    } catch (error) {
      // Silently log error - don't block onboarding flow
      console.error('Error saving onboarding secrets:', error);
      return true; // Continue despite error
    } finally {
      setIsPending(false);
    }
  };

  return {
    saveBusinessTaxSecrets,
    saveCRMCredentials,
    saveAllSecrets,
    isPending,
  };
};
